import React, { useEffect, useMemo, useState } from "react";
import style from "../../styles/grid.module.css";

import {
   ResponsiveContainer,
   LineChart,
   Line,
   CartesianGrid,
   XAxis,
   YAxis,
   Tooltip,
   Legend,
} from "recharts";
import styles from "../../styles/panel.module.css";
import { useMutation } from "@tanstack/react-query";

const numberFormat = new Intl.NumberFormat("es-AR", {
   minimumFractionDigits: 0,
   maximumFractionDigits: 0,
});

const dataSourceInitial = [];

const monthLabel = {
   "01": "Ene",
   "02": "Feb",
   "03": "Mar",
   "04": "Abr",
   "05": "May",
   "06": "Jun",
   "07": "Jul",
   "08": "Ago",
   "09": "Sep",
   "10": "Oct",
   "11": "Nov",
   "12": "Dic",
};

const reportFilterTitles = {
   completo: null,
   tattoo: ["TattooInsumos", "Tattoo", "VentasT", "Ventas", "Gastos"],
   piercing: ["Piercing", "Productos", "VentasP", "Ventas", "Gastos"],
};

function formatMoney(value) {
   return `$ ${numberFormat.format(value || 0)}`;
}

function Panel() {
   const [cotDolar, setDolar] = useState(0);
   const [vergrafico, setVerGrafico] = useState(false);
   const [reportFilter, setReportFilter] = useState("completo");
   const [visibleSeries, setVisibleSeries] = useState({
      Ventas: true,
      VentasT: true,
      VentasP: true,
      TattooInsumos: true,
      Piercing: true,
      Productos: true,
      Tattoo: true,
      Gastos: true,
      Utilidad: true,
   });
   const [dataSource, setDataSource] = useState(dataSourceInitial);
   const [input, setInput] = useState({
      fdesde: getFirstDayOfMonth(),
      fhasta: getToday(),
   });

   const token = localStorage.getItem("token");
   const url = process.env.REACT_APP_URL_DIR + "balance";

   const getDolar = async () => {
      try {
         const response = await fetch(
            "https://api.bluelytics.com.ar/v2/latest"
         );
         const dolar = await response.json();
         setDolar(dolar.blue.value_sell);
      } catch (error) {
         console.log("error: ", error);
         setDolar(0);
      }
   };

   useEffect(() => {
      getDolar();
   }, []);

   const { mutate } = useMutation({
      mutationFn: (datos) =>
         fetch(`${url}?desde=${input.fdesde}&hasta=${input.fhasta}`, {
            method: "GET",
            headers: {
               Authorization: `Bearer ${token}`,
               "Content-Type": "application/json",
            },
         }).then((res) => res.json()),
      onSuccess: (data) => {
         console.log("info: ", data);

         const normalizedData = Array.isArray(data)
         ? data.map((item) => {
             const normalizedTitle =
                item?.titulo === "TattooInsumos" ? "TattooInsumos" : item?.titulo;

             if (item?.titulo === "Productos") {
                    const totalNumber = parseFloat(item.total) || 0;
                    const updatedTotal = cotDolar
                       ? totalNumber * cotDolar
                       : totalNumber;

                    return {
                       ...item,
                       titulo: normalizedTitle,
                       total: updatedTotal,
                    };
                 }

                 return {
                    ...item,
                    titulo: normalizedTitle,
                 };
                })
                : [];
                
                console.log('normalizedData: ', normalizedData);
         setDataSource(normalizedData);
      },
      onError: (error) => {
         console.log("error: ", error);
         setDataSource([]);
         alert("Error");
      },
   });

   const procesar = (e) => {
      e.preventDefault();
      setVerGrafico(true);
      mutate(input);
   };

   const filteredDataSource = useMemo(() => {
      const allowedTitles = reportFilterTitles[reportFilter];

      if (!allowedTitles) {
         return dataSource;
      }

      return dataSource.filter((item) => allowedTitles.includes(item.titulo));
   }, [dataSource, reportFilter]);

   const allowedSeries = useMemo(() => {
      if (reportFilter === "completo") {
         return {
            Ventas: true,
            VentasT: true,
            VentasP: true,
            TattooInsumos: true,
            Piercing: true,
            Productos: true,
            Tattoo: true,
            Gastos: true,
            Utilidad: true,
         };
      }

      if (reportFilter === "tattoo") {
         return {
            Ventas: true,
            VentasT: true,
            VentasP: false,
            TattooInsumos: true,
            Piercing: false,
            Productos: false,
            Tattoo: true,
            Gastos: true,
            Utilidad: true,
         };
      }

      return {
         Ventas: true,
         VentasT: false,
         VentasP: true,
         TattooInsumos: false,
         Piercing: true,
         Productos: true,
         Tattoo: false,
         Gastos: true,
         Utilidad: true,
      };
   }, [reportFilter]);

   // useEffect(() => {
   //    setVisibleSeries(allowedSeries);
   // }, [allowedSeries]);

   const dashboard = useMemo(() => {
      const gastosByMonth = dataSource.reduce((acc, item) => {
         if (item.titulo === "Gastos") {
            const monthKey = item.fecha;
            acc[monthKey] = (acc[monthKey] || 0) + (parseFloat(item.total) || 0);
         }
         return acc;
      }, {});

      const totalGastos = Object.values(gastosByMonth).reduce(
         (acc, value) => acc + (parseFloat(value) || 0),
         0,
      );

      const groupedByMonth = filteredDataSource.reduce((acc, item) => {
         const monthKey = item.fecha;
         if (!acc[monthKey]) {
            acc[monthKey] = {
               fecha: monthKey,
               mes: item.mes,
               mmss: monthLabel[item.mes] || item.mes,
               Gastos: gastosByMonth[monthKey] || 0,
               Piercing: 0,
               Productos: 0,
               Tattoo: 0,
               TattooInsumos:0,
               Ventas: 0,
               VentasP: 0,
               VentasT: 0,
            };
        }

         acc[monthKey][item.titulo] = parseFloat(item.total);
         return acc;
      }, {});

      const monthlyData = Object.values(groupedByMonth).sort((a, b) =>
         a.fecha.localeCompare(b.fecha),
      );

      const totalByTitle = filteredDataSource.reduce((acc, item) => {
         acc[item.titulo] = (acc[item.titulo] || 0) + parseFloat(item.total);
         return acc;
      }, {});

      const totalVentas = totalByTitle.Ventas || 0;
      const totalCosto =
         totalGastos +
         (totalByTitle.Tattoo || 0) +
         (totalByTitle.Piercing || 0) +
         (totalByTitle.Productos || 0) +
         (totalByTitle.TattooInsumos || 0);
         
         console.log('monthlyData: ', monthlyData);
      return {
         monthlyData: monthlyData.map((item) => ({
            ...item,
            Gastos: gastosByMonth[item.fecha] || 0,
            Utilidad:
               (item.Ventas || 0) -
               ((gastosByMonth[item.fecha] || 0) +
               (item.Tattoo || 0) +
               (item.Piercing || 0) +
               (item.Productos || 0) +
               (item.TattooInsumos || 0)),
            }
         )
      ),
      
         totalVentas,
         totalCosto,
         utilidadNeta: totalVentas - totalCosto,
         ventasTattoo: totalByTitle.VentasT || 0,
         ventasPiercing: totalByTitle.VentasP || 0,
      };
   }, [filteredDataSource, dataSource]);

   function getToday() {
      const d = new Date();
      return d.toISOString().slice(0, 10);
   }
   function getFirstDayOfMonth() {
      const d = new Date();
      d.setDate(1);
      return d.toISOString().slice(0, 10);
   }

   function handleChange(e) {
      e.preventDefault();
      setInput({
         ...input,
         [e.target.name]: e.target.value,
      });
   }

   function toggleSeries(seriesKey) {
      setVisibleSeries((prev) => ({
         ...prev,
         [seriesKey]: !prev[seriesKey],
      }));
   }

   function getHeaderStyle(seriesKey) {
      return {
         cursor: "pointer",
         userSelect: "none",
         color: visibleSeries[seriesKey] ? "#111827" : "#9ca3af",
      };
   }

   if (!vergrafico) {
      return (
         <div className={styles.wrapper}>
            <div className={styles.header}>
               <h1 className={styles.title}>Balance</h1>
               <p className={styles.subtitle}>
                  Resumen financiero • Selecciona un rango de fechas para visualizar el reporte
               </p>
            </div>
            <div className={style.fecha_group}>
               <span className={style.titledate}>Fecha desde: </span>
               <input
                  className={style.inputdate}
                  type="date"
                  name="fdesde"
                  value={input.fdesde}
                  onChange={handleChange}
                  required
               />
               &nbsp;&nbsp;&nbsp;&nbsp;
               <span className={style.titledate}>Fecha hasta: </span>
               <input
                  className={style.inputdate}
                  type="date"
                  name="fhasta"
                  value={input.fhasta}
                  onChange={handleChange}
                  required
               />
               &nbsp;&nbsp;&nbsp;
               <button
                  className={style.inputBtnNew}
                  onClick={(e) => procesar(e)}
               >
                  Procesar
               </button>
            </div>
            {/* <button
               className={styles.button}
            >
               Ver Gráfico
            </button> */}
         </div>
      );
   } else {
      return (
         <div className={styles.wrapper}>
            <div className={styles.header}>
               <h1 className={styles.title}>Balance</h1>
               {/* <p className={styles.subtitle}>
                  Resumen mensual • Enero a Marzo 2026
               </p> */}
            </div>

            <section className={styles.kpiGrid}>
               <article className={styles.kpiCard}>
                  <span>Ventas totales</span>
                  <strong>{formatMoney(dashboard.totalVentas)}</strong>
               </article>
               <article className={styles.kpiCard}>
                  <span>Costos totales</span>
                  <strong>{formatMoney(dashboard.totalCosto)}</strong>
               </article>
               <article className={styles.kpiCard}>
                  <span>Utilidad neta</span>
                  <strong>{formatMoney(dashboard.utilidadNeta)}</strong>
               </article>
               <article className={styles.kpiCard}>
                  <span>Ventas Tattoo / Piercing</span>
                  <strong>
                     {formatMoney(dashboard.ventasTattoo)} /{" "}
                     {formatMoney(dashboard.ventasPiercing)}
                  </strong>
               </article>
            </section>

            <section className={styles.chartCard}>
               <h2>Evolución mensual</h2>
               <ResponsiveContainer width="100%" height={320}>
                  <LineChart
                     data={dashboard.monthlyData}
                     margin={{ top: 8, right: 16, left: 28, bottom: 8 }}
                  >
                     <CartesianGrid strokeDasharray="3 3" />
                     <XAxis dataKey="mmss" />
                     <YAxis
                        width={90}
                        tickMargin={8}
                        tickFormatter={(value) => numberFormat.format(value)}
                     />
                     <Tooltip formatter={(value) => formatMoney(value)} />
                     <Legend />
                     {allowedSeries.Ventas && visibleSeries.Ventas && (
                        <Line
                           type="monotone"
                           dataKey="Ventas"
                           stroke="#2f855a"
                           strokeWidth={2}
                        />
                     )}
                     {allowedSeries.VentasT && visibleSeries.VentasT && (
                        <Line
                           type="monotone"
                           dataKey="VentasT"
                           stroke="#0ea5e9"
                           strokeWidth={2}
                        />
                     )}
                     {allowedSeries.VentasP && visibleSeries.VentasP && (
                        <Line
                           type="monotone"
                           dataKey="VentasP"
                           // stroke="#7c3aed"
                           stroke="#f59e0b"
                           strokeWidth={2}
                        />
                     )}
                     {allowedSeries.Gastos && visibleSeries.Gastos && (
                        <Line
                           type="monotone"
                           dataKey="Gastos"
                           stroke="#c53030"
                           strokeWidth={2}
                        />
                     )}
                     {allowedSeries.Tattoo && visibleSeries.Tattoo && (
                        <Line
                           type="monotone"
                           dataKey="Tattoo"
                           stroke="#2b6cb0"
                           strokeWidth={2}
                        />
                     )}
                     {allowedSeries.TattooInsumos && visibleSeries.TattooInsumos && (
                        <Line
                           type="monotone"
                           dataKey="TattooInsumos"
                           stroke="#ec4899"
                           strokeWidth={2}
                        />
                     )}
                     {allowedSeries.Piercing && visibleSeries.Piercing && (
                        <Line
                           type="monotone"
                           dataKey="Piercing"
                           stroke="#805ad5"
                           strokeWidth={2}
                        />
                     )}
                     {allowedSeries.Productos && visibleSeries.Productos && (
                        <Line
                           type="monotone"
                           dataKey="Productos"
                           stroke="#10b981"
                           strokeWidth={2}
                        />
                     )}
                     {allowedSeries.Utilidad && visibleSeries.Utilidad && (
                        <Line
                           type="monotone"
                           dataKey="Utilidad"
                           stroke="#1a202c"
                           strokeWidth={3}
                        />
                     )}
                  </LineChart>
               </ResponsiveContainer>
            </section>

            <section className={styles.tableCard}>
               <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"10px"}}>
                  <h2>Detalle por mes</h2>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                     <select
                        className={style.inputdate}
                        value={reportFilter}
                        onChange={(e) => setReportFilter(e.target.value)}
                        aria-label="Filtrar reporte"
                     >
                        <option value="completo">Completo</option>
                        <option value="piercing">Piercing</option>
                        <option value="tattoo">Tattoo</option>
                     </select>
                     <button
                        className={style.inputBtnNew}
                        onClick={() => setVerGrafico(false)}
                     >
                        Cambiar Fechas
                     </button>
                  </div>
               </div>
               <div className={styles.tableScroll}>
                  <table className={styles.table}>
                     <thead>
                        <tr>
                           <th>Mes</th>
                           <th
                              onClick={() => toggleSeries("Ventas")}
                              style={getHeaderStyle("Ventas")}
                              title="Mostrar/Ocultar Ventas en el gráfico"
                             
                           >
                              Ventas {visibleSeries.Ventas ? "✓" : "✕"}
                           </th>
                           {allowedSeries.VentasT && (
                           <th
                              onClick={() => toggleSeries("VentasT")}
                              style={getHeaderStyle("VentasT")}
                              title="Mostrar/Ocultar Ventas Tattoo en el gráfico"
                           >
                              Ventas Tattoo {visibleSeries.VentasT ? "✓" : "✕"}
                           </th>
                           )}
                           {allowedSeries.VentasP && (
                           <th
                              onClick={() => toggleSeries("VentasP")}
                              style={getHeaderStyle("VentasP")}
                              title="Mostrar/Ocultar Ventas Piercing en el gráfico"
                           >
                              Ventas Piercing {visibleSeries.VentasP ? "✓" : "✕"}
                           </th>
                           )}
                           {allowedSeries.TattooInsumos && (
                           <th
                              onClick={() => toggleSeries("TattooInsumos")}
                              style={getHeaderStyle("TattooInsumos")}
                              title="Mostrar/Ocultar Insumos Tattoo en el gráfico"
                           >
                              Insumos Tattoo {visibleSeries.TattooInsumos ? "✓" : "✕"}
                           </th>
                           )}
                           {allowedSeries.Piercing && (
                           <th
                              onClick={() => toggleSeries("Piercing")}
                              style={getHeaderStyle("Piercing")}
                              title="Mostrar/Ocultar Insumos Piercing en el gráfico"
                           >
                              Insumos Piercing {visibleSeries.Piercing ? "✓" : "✕"}
                           </th>
                           )}
                           {allowedSeries.Productos && (
                           <th
                              onClick={() => toggleSeries("Productos")}
                              style={getHeaderStyle("Productos")}
                              title="Mostrar/Ocultar Productos Pier en el gráfico"
                           >
                              Productos Pier {visibleSeries.Productos ? "✓" : "✕"}
                           </th>
                           )}
                           {allowedSeries.Tattoo && (
                           <th
                              onClick={() => toggleSeries("Tattoo")}
                              style={getHeaderStyle("Tattoo")}
                              title="Mostrar/Ocultar Productos Tatt en el gráfico"
                           >
                              Productos Tatt {visibleSeries.Tattoo ? "✓" : "✕"}
                           </th>
                           )}
                           {allowedSeries.Gastos && (
                           <th
                              onClick={() => toggleSeries("Gastos")}
                              style={getHeaderStyle("Gastos")}
                              title="Mostrar/Ocultar Gastos en el gráfico"
                           >
                              Gastos {visibleSeries.Gastos ? "✓" : "✕"}
                           </th>
                           )}
                           <th
                              onClick={() => toggleSeries("Utilidad")}
                              style={getHeaderStyle("Utilidad")}
                              title="Mostrar/Ocultar Utilidad en el gráfico"
                           >
                              Utilidad {visibleSeries.Utilidad ? "✓" : "✕"}
                           </th>
                        </tr>
                     </thead>
                     <tbody>
                        {dashboard.monthlyData.map((row) => (
                           <tr key={row.fecha}>
                              <td>{row.mmss}</td>
                              <td style={{ color: "#2f855a" }}>
                                 {formatMoney(row.Ventas)}
                              </td>
                              {allowedSeries.VentasT && (
                              <td style={{color:"#0ea5e9"}}>{formatMoney(row.VentasT)}</td>
                              )}
                              {allowedSeries.VentasP && (
                              <td style={{color:"#f59e0b"}}>{formatMoney(row.VentasP)}</td>
                              )}
                              {allowedSeries.TattooInsumos && (
                              <td style={{color:"#ec4899"}}>{formatMoney(row.TattooInsumos)}</td>
                              )}
                              {allowedSeries.Piercing && (
                              <td style={{color:"#8b5cf6"}}>{formatMoney(row.Piercing)}</td>
                              )}
                              {allowedSeries.Productos && (
                              <td style={{color:"#10b981"}}>{formatMoney(row.Productos)}</td>
                              )}
                              {allowedSeries.Tattoo && (
                              <td style={{color:"#2b6cb0"}}>{formatMoney(row.Tattoo)}</td>
                              )}
                              {allowedSeries.Gastos && (
                              <td style={{color:"#ef4444"}}>{formatMoney(row.Gastos)}</td>
                              )}
                              <td className={styles.utilidad}>
                                 {formatMoney(row.Utilidad)}
                              </td>
                           </tr>
                        ))}
                     </tbody>
                  </table>
               </div>
            </section>
         </div>
      );
   }
}

export default Panel;
